<template>
  <div class="fu-fbtn">
    <button
      ref="button"
      type="button"
      class="fu-fbtn__btn"
      :class="[`fu-fbtn__btn--${variant}`, { 'fu-fbtn__btn--active': active }]"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="dialog"
      v-bind="$attrs"
      @click="toggle"
    >
      <fu-icon v-if="icon" :name="icon" :size="16" class="fu-fbtn__icon" />
      <span class="fu-fbtn__label">{{ label }}</span>
      <fu-icon v-if="chevron" name="chev" :size="15" class="fu-fbtn__chev" />
    </button>

    <followups-filter-popover
      v-if="open && popover"
      :anchor="rect"
      v-bind="popover"
      @apply="onApply"
      @select="onSelect"
      @close="close(true)"
    />
  </div>
</template>

<script>
/**
 * A toolbar / header button that opens one FollowupsFilterPopover (the export's filter buttons,
 * lines 356–396, and the "Your lists" button, line 83). It owns only whether its popover is open
 * and where its button is; everything else is a prop, every result an event:
 *
 *   <followups-filter-button icon="cal" :label="dateLabel" data-test="fu-filter-date"
 *     :popover="{ kind: 'date', title: 'Recall due date', from, to, today }"
 *     @apply="range => …" />
 *
 * `popover` is passed straight to FollowupsFilterPopover (kind, title, width, estHeight, align, …).
 * Without it the button is a plain button that emits `click` (e.g. "More filters" opening a sheet).
 * Attributes (data-test, aria-label) land on the <button>, not the wrapper. Apply / select close
 * the popover and are re-emitted; closing gives focus back to the button.
 */
import FuIcon from './FuIcon.vue';
import FollowupsFilterPopover from './FollowupsFilterPopover.vue';

export default {
  name: 'FollowupsFilterButton',
  components: { FuIcon, FollowupsFilterPopover },
  inheritAttrs: false,
  props: {
    label: { type: String, required: true },
    icon: { type: String, default: '' },
    chevron: { type: Boolean, default: true },
    variant: {
      type: String,
      default: 'secondary',
      validator: v => ['secondary', 'ghost'].indexOf(v) !== -1,
    },
    active: { type: Boolean, default: false },
    popover: { type: Object, default: null },
  },

  data() {
    return { open: false, rect: null };
  },

  methods: {
    toggle() {
      if (!this.popover) {
        this.$emit('click');
        return;
      }
      if (this.open) {
        this.close(false);
        return;
      }
      const r = this.$refs.button.getBoundingClientRect();
      this.rect = { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height };
      this.open = true;
      this.$emit('open');
    },
    close(restoreFocus) {
      if (!this.open) return;
      this.open = false;
      this.$emit('close');
      if (restoreFocus && this.$refs.button) this.$nextTick(() => this.$refs.button && this.$refs.button.focus());
    },
    onApply(payload) {
      this.close(true);
      this.$emit('apply', payload);
    },
    onSelect(item) {
      this.close(false);
      this.$emit('select', item);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-fbtn {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
}

.fu-fbtn__btn {
  max-width: 100%;
  min-width: 0;
}

.fu-fbtn__btn--secondary {
  @include fu-btn(secondary, md);
}

.fu-fbtn__btn--ghost {
  @include fu-btn(ghost, md);
}

.fu-fbtn__btn--active {
  border-color: $fu-brand-300;
  background: $fu-brand-50;
  color: $fu-brand-700;
  &:hover:not([disabled]) { background: $fu-brand-100; }
  .fu-fbtn__icon { color: $fu-brand-700; }
}

.fu-fbtn__icon {
  flex: none;
  color: $fu-text-muted;
}

.fu-fbtn__label {
  @include fu-ellipsis;
  min-width: 0;
}

.fu-fbtn__chev {
  flex: none;
  margin-left: -2px;
  color: $fu-text-muted;
}
</style>
